import { useState, useCallback, useEffect } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Link, useLocation } from "wouter";
import { useTranslation } from "react-i18next";
import { useGetCurrentUser } from "@workspace/api-client-react";
import { useAuth } from "@clerk/react";
import { useWebSignOut } from "@/hooks/use-web-sign-out";
import { PlateOSLogo } from "@/components/plate-os-logo";
import { useTheme, COLOR_SCHEME_META } from "@/lib/theme";
import { useGroceryStore } from "@/lib/grocery-store";
import { useLiteMode } from "@/lib/lite-mode";
import { usePlateTalkStore } from "@/lib/plate-talk-store";
import { openDashboardModal } from "@/lib/dashboard-modal-store";
import {
  User,
  LogOut,
  Sun,
  Moon,
  X,
  MoreHorizontal,
  Crown,
  ShieldAlert,
  Lock,
  ChevronDown,
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { NotificationBell } from "@/components/notification-bell";
import { GettingStartedChecklist } from "@/components/onboarding/getting-started-checklist";
import { markPlateTalkVisited, markHydrationViewed } from "@/hooks/use-activation-status";
import {
  NAV_GROUPS,
  NAV_ITEMS,
  PRO_HREFS,
  FULL_MODE_DEFAULT_OPEN,
  REVEAL_TOUR_NAV_TARGETS_EVENT,
  isProUser,
  findGroupId,
  findGroupIdForTourId,
  tourId,
  type NavItem,
} from "./nav-config";
import { AdminModeControl } from "@/components/delegation-controls";

const OPEN_OVERRIDES_SUFFIX = "sidebarOpenOverrides";

function openOverridesStorageKey(uid: string | null | undefined) {
  return `plateOS:${uid ?? "anon"}:${OPEN_OVERRIDES_SUFFIX}`;
}

function readOpenOverrides(uid: string | null | undefined): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(openOverridesStorageKey(uid));
    if (!raw) return {};

    const value: unknown = JSON.parse(raw);
    if (!value || typeof value !== "object" || Array.isArray(value)) return {};

    return Object.fromEntries(
      Object.entries(value).filter((entry): entry is [string, boolean] => typeof entry[1] === "boolean"),
    );
  } catch {
    return {};
  }
}

function writeOpenOverrides(uid: string | null | undefined, overrides: Record<string, boolean>) {
  try {
    localStorage.setItem(openOverridesStorageKey(uid), JSON.stringify(overrides));
  } catch {
    /* localStorage unavailable — ignore */
  }
}

export function ThemePicker({ compact = false }: { compact?: boolean }) {
  const { t } = useTranslation();
  const { theme, colorScheme, toggleTheme, setColorScheme } = useTheme();

  return (
    <div data-testid="theme-picker" className={compact ? "flex items-center gap-2" : "flex flex-col gap-2"}>
      <div className="flex items-center gap-1.5">
        {COLOR_SCHEME_META.map((s) => (
          <button
            key={s.id}
            onClick={() => setColorScheme(s.id)}
            title={s.label}
            aria-label={`${s.label} ${t("sidebar.colorTheme")}`}
            className={`theme-picker-focus w-6 h-6 rounded-full border-2 transition-all duration-200 shrink-0 ${
              colorScheme === s.id
                ? "border-foreground scale-110 shadow-[0_0_8px_1px_var(--tw-shadow-color)]"
                : "border-transparent hover:border-muted-foreground/40 hover:scale-105"
            }`}
            style={{
              backgroundColor: s.swatch,
              ["--tw-shadow-color" as string]: s.swatch,
            }}
          />
        ))}
      </div>
      <button
        onClick={toggleTheme}
        className="theme-picker-focus w-8 h-8 flex items-center justify-center rounded-md text-muted-foreground hover:text-primary hover:bg-primary/10 transition-all duration-200 hover:shadow-[0_0_12px_-2px_hsl(var(--primary)/0.5)]"
        title={theme === "dark" ? t("sidebar.switchToLight") : t("sidebar.switchToDark")}
      >
        {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
      </button>
    </div>
  );
}

interface SidebarNavItemProps {
  item: NavItem;
  isLite: boolean;
  isPro: boolean;
  indent?: boolean;
  onNavigate: (href: string, e: React.MouseEvent) => void;
}

function SidebarNavItem({ item, isLite, isPro, indent = false, onNavigate }: SidebarNavItemProps) {
  const { t } = useTranslation();
  const [location] = useLocation();
  const { completedWhileAway: plateTalkNotification, clearCompletedFlag: clearPlateTalkNotification } = usePlateTalkStore();
  const isActive = location === item.href;

  return (
    <Link
      key={item.href}
      href={item.href === "/dashboard" ? location : item.href}
      onClick={(e: React.MouseEvent) => {
        if (item.href === "/dashboard") {
          e.preventDefault();
          markHydrationViewed();
          openDashboardModal();
          return;
        }
        onNavigate(item.href, e);
        if (item.href === "/plate-talk") {
          markPlateTalkVisited();
          if (plateTalkNotification && !e.defaultPrevented) clearPlateTalkNotification();
        }
      }}
      data-tour={tourId(item.href)}
      className={`
        flex items-center gap-3 ${indent ? "ml-3 pl-4 pr-3 border-l border-sidebar-border/60" : "px-3"} ${isLite ? "py-3" : "py-2.5"} rounded-lg transition-all duration-200 group
        ${isActive
          ? "bg-primary/12 text-primary border border-primary/25 shadow-[0_0_14px_-3px_hsl(var(--primary)/0.35)]"
          : "text-sidebar-foreground hover:bg-primary/8 hover:text-primary hover:shadow-[0_0_12px_-3px_hsl(var(--primary)/0.25)] border border-transparent hover:border-primary/15"
        }
      `}
    >
      <div className="relative shrink-0">
        <item.icon className={`${isLite && !indent ? "w-5 h-5" : "w-4 h-4"} transition-all ${isActive ? "text-primary" : "text-muted-foreground group-hover:text-primary"}`} />
        {item.href === "/plate-talk" && plateTalkNotification && !isActive && (
          <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-red-500 border-2 border-sidebar animate-pulse" />
        )}
      </div>
      <div className="flex flex-col min-w-0 flex-1">
        <span className={`${isLite && !indent ? "text-[15px]" : "text-sm"} font-medium leading-tight`}>{t(item.tLabel)}</span>
        <span className={`${isLite && !indent ? "text-[11px]" : "text-[10px]"} leading-tight truncate ${isActive ? "text-primary/60" : "text-muted-foreground/60 group-hover:text-primary/50"}`}>{t(item.tDesc)}</span>
      </div>
      {!isPro && PRO_HREFS.has(item.href) && (
        <Lock className="w-3 h-3 text-muted-foreground/50 shrink-0" aria-label={t("sidebar.proLocked")} />
      )}
    </Link>
  );
}

export function Sidebar() {
  const { t } = useTranslation();
  const [location, setLocation] = useLocation();
  const { data: user, isLoading } = useGetCurrentUser();
  const { isLite } = useLiteMode();
  const { userId } = useAuth();
  const { handleSignOut, isSigningOut, signOutError } = useWebSignOut();
  const grocery = useGroceryStore();
  // Per-group user override: true = user opened, false = user collapsed.
  const [openOverrides, setOpenOverrides] = useState<Record<string, boolean>>(() => readOpenOverrides(userId));

  const isPro = isProUser(user);
  const activeGroupId = findGroupId(location);

  // Restore a different signed-in user's preferences without carrying over
  // the prior user's state in this still-mounted app shell.
  useEffect(() => {
    setOpenOverrides(readOpenOverrides(userId));
  }, [userId]);

  // Auto-reveal the section of the page the user just navigated to, even if
  // they had previously collapsed it. Removing the stale override also means
  // its next visit follows the normal mode default rather than collapsing.
  useEffect(() => {
    if (!activeGroupId || activeGroupId === "everyday") return;
    setOpenOverrides((prev) => {
      if (prev[activeGroupId] !== false) return prev;
      const next = { ...prev };
      delete next[activeGroupId];
      writeOpenOverrides(userId, next);
      return next;
    });
  }, [activeGroupId, openOverrides, userId]);

  // Tour links in collapsed groups are not mounted. Reveal every requested
  // group before Joyride performs its target lookup.
  useEffect(() => {
    const revealTourTargetGroups = (event: Event) => {
      const targetIds = (event as CustomEvent<{ targetIds?: readonly string[] }>).detail?.targetIds ?? [];
      const groupIds = new Set(
        targetIds
          .map(findGroupIdForTourId)
          .filter((groupId): groupId is string => Boolean(groupId) && groupId !== "everyday"),
      );

      if (groupIds.size === 0) return;

      setOpenOverrides((previous) => {
        let next = previous;
        for (const groupId of groupIds) {
          if (previous[groupId] === true) continue;
          if (next === previous) next = { ...previous };
          next[groupId] = true;
        }
        return next;
      });
    };

    window.addEventListener(REVEAL_TOUR_NAV_TARGETS_EVENT, revealTourTargetGroups);
    return () => window.removeEventListener(REVEAL_TOUR_NAV_TARGETS_EVENT, revealTourTargetGroups);
  }, []);

  const isGroupOpen = useCallback((id: string) => {
    if (id === activeGroupId) return true;
    if (id in openOverrides) return openOverrides[id];
    return !isLite && FULL_MODE_DEFAULT_OPEN.has(id);
  }, [openOverrides, activeGroupId, isLite]);

  const toggleGroup = useCallback((id: string) => {
    setOpenOverrides((prev) => {
      const next = { ...prev, [id]: !isGroupOpen(id) };
      writeOpenOverrides(userId, next);
      return next;
    });
  }, [isGroupOpen, userId]);

  const guardedNavigate = useCallback((href: string, e: React.MouseEvent) => {
    if (location === "/grocery" && grocery.isBusy && href !== "/grocery") {
      e.preventDefault();
      const confirmed = window.confirm(t("sidebar.groceryNavGuard"));
      if (confirmed) setLocation(href);
    }
  }, [location, grocery.isBusy, setLocation, t]);

  if (isLoading || !user) return null;

  const everydayGroup = NAV_GROUPS[0];
  const secondaryGroups = NAV_GROUPS.slice(1);

  return (
    <aside className="w-64 border-r border-sidebar-border bg-sidebar h-screen flex-col hidden md:flex shrink-0">
      <div className="px-5 py-5 flex items-center justify-between border-b border-sidebar-border">
        <div className="flex items-center gap-2.5">
          <PlateOSLogo size={28} />
          <span className="text-base font-bold tracking-tight text-foreground">{t("sidebar.plateOs")}</span>
        </div>
        <NotificationBell />
      </div>

      <div className="px-4 py-3 border-b border-sidebar-border">
        <ThemePicker compact />
        {user.role === "admin" && <div className="mt-3"><AdminModeControl /></div>}
      </div>

      <div className="shrink-0 max-h-[30vh] overflow-y-auto">
        <GettingStartedChecklist />
      </div>

      <nav data-tour="sidebar-nav" className="flex-1 min-h-0 py-4 flex flex-col gap-0.5 px-3 overflow-y-auto">
        {/* Everyday destinations stay immediately visible in both modes. */}
        <div className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
          {t(everydayGroup.tLabel)}
        </div>
        {everydayGroup.items.map((item) => (
          <SidebarNavItem key={item.href} item={item} isLite={isLite} isPro={isPro} onNavigate={guardedNavigate} />
        ))}

        {secondaryGroups.map((group) => {
          const open = isGroupOpen(group.id);
          return (
            <div key={group.id} className="mt-1.5">
              <button
                type="button"
                onClick={() => toggleGroup(group.id)}
                aria-expanded={open}
                aria-controls={`sidebar-group-${group.id}`}
                data-testid={`sidebar-group-toggle-${group.id}`}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 group text-sidebar-foreground hover:bg-primary/8 hover:text-primary border border-transparent hover:border-primary/15"
              >
                <div className="flex flex-col min-w-0 flex-1 text-left">
                  <span className="text-xs font-semibold uppercase tracking-wider leading-tight text-muted-foreground group-hover:text-primary">{t(group.tLabel)}</span>
                  {!open && (
                    <span className="text-[10px] leading-tight truncate text-muted-foreground/60 group-hover:text-primary/50">{t(group.tDesc)}</span>
                  )}
                </div>
                <ChevronDown className={`w-4 h-4 shrink-0 text-muted-foreground/70 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
              </button>
              {open && (
                <div id={`sidebar-group-${group.id}`} role="group" aria-label={t(group.tLabel)} className="flex flex-col gap-0.5">
                  {group.items.map((item) => (
                    <SidebarNavItem key={item.href} item={item} isLite={isLite} isPro={isPro} indent onNavigate={guardedNavigate} />
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {user.role === "admin" && (
          <Link
            href="/admin"
            data-tour="nav-admin"
            className={`
              mt-1 flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 group
              ${location === "/admin" || location.startsWith("/admin/")
                ? "bg-primary/12 text-primary border border-primary/25 shadow-[0_0_14px_-3px_hsl(var(--primary)/0.35)]"
                : "text-sidebar-foreground hover:bg-primary/8 hover:text-primary hover:shadow-[0_0_12px_-3px_hsl(var(--primary)/0.25)] border border-transparent hover:border-primary/15"
              }
            `}
          >
            <div className="relative shrink-0">
              <ShieldAlert className={`w-4 h-4 transition-all ${location.startsWith("/admin") ? "text-primary" : "text-muted-foreground group-hover:text-primary"}`} />
            </div>
            <div className="flex flex-col min-w-0">
              <span className="text-sm font-medium leading-tight">Command Center</span>
              <span className="text-[10px] leading-tight truncate text-muted-foreground/60 group-hover:text-primary/50">Operations dashboard</span>
            </div>
          </Link>
        )}
      </nav>

      <div className="px-4 py-4 border-t border-sidebar-border">
        <div className="flex items-center gap-3 mb-3">
          <Avatar className="h-9 w-9 border border-primary/25 shrink-0">
            <AvatarImage src={user.avatarUrl || undefined} />
            <AvatarFallback className="bg-primary/10 text-primary text-sm font-semibold">
              {(user.firstName?.trim()?.[0] ?? "•").toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="overflow-hidden flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <p className="text-sm font-semibold truncate text-foreground">{user.firstName?.trim() || t("profile.nicknameFallback")}</p>
              {isProUser(user) && (
                <span className="shrink-0 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-500">
                  <Crown className="w-2.5 h-2.5" />
                  <span className="text-[9px] font-bold uppercase tracking-wider">{t(user.accessPolicy?.temporaryFree ? "accessPolicy.title" : "sidebar.pro")}</span>
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground truncate">{user.email}</p>
          </div>
        </div>
        <Button
          variant="ghost"
          className="w-full text-sm font-medium text-muted-foreground hover:text-destructive hover:bg-destructive/8 justify-start gap-2 h-9"
          onClick={() => void handleSignOut()}
          disabled={isSigningOut}
        >
          <LogOut className="w-4 h-4" />
          {t("sidebar.signOut")}
        </Button>
        {signOutError && <p role="alert" className="text-sm text-destructive">{t("common.error")}. {t("common.retry")}</p>}
        <div className="mt-3 pt-3 border-t border-sidebar-border/60 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[10px] text-muted-foreground/60" data-testid="sidebar-legal-links">
          <Link href="/legal/privacy" className="hover:text-primary transition-colors">{t("legal.privacyPolicy")}</Link>
          <span className="opacity-40">·</span>
          <Link href="/legal/terms" className="hover:text-primary transition-colors">{t("legal.termsOfService")}</Link>
          <span className="opacity-40">·</span>
          <Link href="/legal/disclaimer" className="hover:text-primary transition-colors">{t("legal.healthDisclaimer")}</Link>
        </div>
      </div>
    </aside>
  );
}

// Bottom-bar destinations. Focused mode keeps its original concise set; full
// mode promotes the first Everyday actions instead of an arbitrary slice.
const FOCUSED_MOBILE_PRIMARY = ["/dashboard", "/plate-log", "/coach-ai", "/grocery"];
const FULL_MOBILE_PRIMARY = ["/dashboard", "/plate-log", "/coach-ai", "/plate-talk"];

export function MobileNav() {
  const { t } = useTranslation();
  const [location, setLocation] = useLocation();
  const { data: user, isLoading } = useGetCurrentUser();
  const { isLite } = useLiteMode();
  const { handleSignOut, isSigningOut, signOutError } = useWebSignOut();
  const [moreOpen, setMoreOpen] = useState(false);
  const grocery = useGroceryStore();
  const { completedWhileAway: plateTalkNotification, clearCompletedFlag: clearPlateTalkNotification } = usePlateTalkStore();

  useEffect(() => {
    if (!moreOpen || typeof window.matchMedia !== "function") return;
    const desktop = window.matchMedia("(min-width: 768px)");
    const closeOnDesktop = () => { if (desktop.matches) setMoreOpen(false); };
    closeOnDesktop();
    desktop.addEventListener("change", closeOnDesktop);
    return () => desktop.removeEventListener("change", closeOnDesktop);
  }, [moreOpen]);

  const guardedNavigate = useCallback((href: string, e: React.MouseEvent) => {
    if (location === "/grocery" && grocery.isBusy && href !== "/grocery") {
      e.preventDefault();
      const confirmed = window.confirm(t("sidebar.groceryNavGuard"));
      if (confirmed) { setLocation(href); setMoreOpen(false); }
    }
  }, [location, grocery.isBusy, setLocation, t]);

  if (isLoading || !user) return null;

  const primaryHrefs = isLite ? FOCUSED_MOBILE_PRIMARY : FULL_MOBILE_PRIMARY;
  const mobileNavPrimary = primaryHrefs
    .map(h => NAV_ITEMS.find(i => i.href === h))
    .filter((i): i is NavItem => Boolean(i));
  const primarySet = new Set(primaryHrefs);
  const moreGroups = NAV_GROUPS
    .map(g => ({ ...g, items: g.items.filter(i => !primarySet.has(i.href)) }))
    .filter(g => g.items.length > 0);

  const isMoreActive = moreGroups.some(g => g.items.some(item => location === item.href));

  return (
    <DialogPrimitive.Root open={moreOpen} onOpenChange={setMoreOpen}>
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm md:hidden" />
          <DialogPrimitive.Content
            data-testid="mobile-more-sheet"
            aria-describedby={undefined}
            className="fixed z-[71] bottom-16 left-0 right-0 bg-card border-t border-border rounded-t-2xl p-4 pb-2 animate-in slide-in-from-bottom-4 duration-200 flex flex-col max-h-[75vh] md:hidden"
          >
            <div className="flex items-center justify-between mb-3 px-1 shrink-0">
              <DialogPrimitive.Title className="text-sm font-semibold text-foreground">{t("sidebar.more")}</DialogPrimitive.Title>
              <DialogPrimitive.Close asChild>
              <button aria-label={t("common.close")} className="w-[44px] h-[44px] rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors focus-visible:outline-2 focus-visible:outline-primary">
                <X className="w-4 h-4" />
              </button>
              </DialogPrimitive.Close>
            </div>

            <div className="px-1 mb-3 shrink-0">
              <ThemePicker compact />
              {user.role === "admin" && <div className="mt-3"><AdminModeControl mobile /></div>}
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto">
              {moreGroups.map(group => (
                <div key={group.id} className="mb-3" data-testid={`mobile-more-group-${group.id}`}>
                  <div className="px-1 mb-1 flex items-baseline gap-2">
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t(group.tLabel)}</span>
                    <span className="text-[10px] text-muted-foreground/60 truncate">{t(group.tDesc)}</span>
                  </div>
                  <div className="flex flex-col gap-0.5">
                    {group.items.map(item => {
                      const isActive = location === item.href;
                      const locked = !isProUser(user) && PRO_HREFS.has(item.href);
                      return (
                        <Link key={item.href} href={item.href} onClick={(e: React.MouseEvent) => { guardedNavigate(item.href, e); if (!e.defaultPrevented) setMoreOpen(false); }}
                          className={`flex items-center gap-3 px-2 py-2.5 rounded-xl transition-all relative
                            ${isActive ? "bg-primary/12 text-primary" : "text-muted-foreground hover:text-foreground hover:bg-muted/50"}`}>
                          <item.icon className="w-5 h-5 shrink-0" />
                          <div className="flex flex-col min-w-0 flex-1">
                            <span className="text-sm font-medium leading-tight">{t(item.tLabel)}</span>
                            <span className="text-[10px] leading-tight truncate text-muted-foreground/60">{t(item.tDesc)}</span>
                          </div>
                          {locked && (
                            <Lock className="w-3 h-3 text-muted-foreground/60 shrink-0" aria-label={t("sidebar.proLocked")} />
                          )}
                        </Link>
                      );
                    })}
                  </div>
                </div>
              ))}

              {user.role === "admin" && (
                <Link
                  href="/admin"
                  onClick={() => setMoreOpen(false)}
                  className={`mb-3 flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all ${
                    location.startsWith("/admin")
                      ? "bg-primary/12 text-primary"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                  }`}
                >
                  <ShieldAlert className="w-5 h-5 shrink-0" />
                  <div className="flex flex-col min-w-0">
                    <span className="text-sm font-medium leading-tight">Command Center</span>
                    <span className="text-[10px] leading-tight truncate text-muted-foreground/60">Operations dashboard</span>
                  </div>
                </Link>
              )}
            </div>

            <div className="border-t border-border pt-2 shrink-0">
              <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 px-1 py-2 text-[10px] text-muted-foreground/70" data-testid="mobile-legal-links">
                <Link href="/legal/privacy" onClick={() => setMoreOpen(false)} className="hover:text-primary transition-colors">{t("legal.privacyPolicy")}</Link>
                <span className="opacity-40">·</span>
                <Link href="/legal/terms" onClick={() => setMoreOpen(false)} className="hover:text-primary transition-colors">{t("legal.termsOfService")}</Link>
                <span className="opacity-40">·</span>
                <Link href="/legal/disclaimer" onClick={() => setMoreOpen(false)} className="hover:text-primary transition-colors">{t("legal.healthDisclaimer")}</Link>
              </div>
              <div className="flex items-center gap-3 px-1 py-2">
                <Avatar className="h-8 w-8 border border-primary/25 shrink-0">
                  <AvatarImage src={user.avatarUrl || undefined} />
                  <AvatarFallback className="bg-primary/10 text-primary text-xs font-semibold">
                    {(user.firstName?.trim()?.[0] ?? "•").toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <p className="text-sm font-semibold truncate">{user.firstName?.trim() || t("profile.nicknameFallback")}</p>
                    {isProUser(user) && (
                      <span className="shrink-0 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-500">
                        <Crown className="w-2.5 h-2.5" />
                        <span className="text-[9px] font-bold uppercase tracking-wider">{t(user.accessPolicy?.temporaryFree ? "accessPolicy.title" : "sidebar.pro")}</span>
                      </span>
                    )}
                  </div>
                  <p className="text-[10px] text-muted-foreground truncate">{user.email}</p>
                </div>
                <button type="button" onClick={() => void handleSignOut()} disabled={isSigningOut}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors disabled:cursor-wait disabled:opacity-60">
                  <LogOut className="w-3.5 h-3.5" />
                  {t("sidebar.signOut")}
                </button>
              </div>
              {signOutError && <p role="alert" className="text-sm text-destructive">{t("common.error")}. {t("common.retry")}</p>}
            </div>
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>

      <nav data-tour="mobile-nav" data-testid="mobile-nav" className="fixed bottom-0 left-0 right-0 z-30 md:hidden bg-card/95 backdrop-blur-md border-t border-border safe-area-bottom">
        <div className="flex items-stretch">
          {mobileNavPrimary.map(item => {
            const isActive = location === item.href;
            const mobileTourId = "mobile-nav-" + item.href.replace("/", "").replace(/\//g, "-");
            return (
              <Link key={item.href} href={item.href === "/dashboard" ? location : item.href}
                onClick={(e: React.MouseEvent) => {
                  if (item.href === "/dashboard") {
                    e.preventDefault();
                    markHydrationViewed();
                    openDashboardModal();
                    return;
                  }
                  guardedNavigate(item.href, e);
                  if (item.href === "/plate-talk") {
                    markPlateTalkVisited();
                    if (plateTalkNotification && !e.defaultPrevented) clearPlateTalkNotification();
                  }
                }}
                data-tour={mobileTourId}
                className={`min-h-[44px] flex-1 flex flex-col items-center justify-center gap-0.5 py-2 pb-2.5 pt-2.5 transition-all relative
                  ${isActive ? "text-primary" : "text-muted-foreground"}`}>
                {isActive && <div className="absolute top-0 left-1/4 right-1/4 h-0.5 rounded-full bg-primary" />}
                <div className="relative">
                  <item.icon className={`w-5 h-5 ${isActive ? "text-primary" : ""}`} />
                  {item.href === "/plate-talk" && plateTalkNotification && !isActive && (
                    <span className="absolute -top-1 -right-1.5 w-2.5 h-2.5 rounded-full bg-red-500 border-2 border-card animate-pulse" />
                  )}
                  {!isProUser(user) && PRO_HREFS.has(item.href) && (
                    <span className="absolute -top-1.5 -right-2 w-3.5 h-3.5 rounded-full bg-card border border-border flex items-center justify-center">
                      <Lock className="w-2 h-2 text-muted-foreground/70" aria-label={t("sidebar.proLocked")} />
                    </span>
                  )}
                </div>
                <span className={`text-[10px] font-medium leading-tight pb-0.5 ${isActive ? "text-primary" : ""}`}>{t(item.tLabel)}</span>
              </Link>
            );
          })}
          <DialogPrimitive.Trigger asChild>
          <button
            data-tour="mobile-nav-more"
            data-testid="mobile-nav-more"
            className={`min-h-[44px] flex-1 flex flex-col items-center justify-center gap-0.5 py-2 pb-2.5 pt-2.5 transition-all relative
              ${isMoreActive || moreOpen ? "text-primary" : "text-muted-foreground"}`}>
            {(isMoreActive || moreOpen) && <div className="absolute top-0 left-1/4 right-1/4 h-0.5 rounded-full bg-primary" />}
            <MoreHorizontal className="w-5 h-5" />
            <span className="text-[10px] font-medium leading-tight pb-0.5">{t("sidebar.more")}</span>
          </button>
          </DialogPrimitive.Trigger>
        </div>
      </nav>
    </DialogPrimitive.Root>
  );
}
